import axios from "axios";
import { Routing } from "config/enums/routings";
import { Coords, RoutingAPI, RoutingCostingOptions } from "types/app";
import { uninterceptedAxiosInstance } from "utils/axios/axios";
import { decodePolyline } from "utils/polyline";

const NAV_BASE_API_URL = import.meta.env.VITE_NAV_API_BASE;

/** Costing options in the format of the Valhalla API, only the bicycle routing has some */
export type ValhallaCostingOptions = {
  [Routing.bicycle]?: { bicycle_type: string; use_hills: string };
};

export const getCostingOptionsPayload = (
  routing: Routing,
  costingOptions?: RoutingCostingOptions
): ValhallaCostingOptions | undefined =>
  routing === Routing.bicycle && costingOptions?.[Routing.bicycle]
    ? {
        [Routing.bicycle]: {
          bicycle_type: costingOptions[Routing.bicycle].bicycleType,
          use_hills: costingOptions[Routing.bicycle].useHills,
        },
      }
    : undefined;

export type RouteRequest = {
  waypoints: Coords[];
  costing: Routing;
  costingOptions?: ValhallaCostingOptions;
};

/** A part of the route with one instruction of the router, e.g. "turn left and go 300 m" */
export type RouteManeuver = {
  /** The indices of the route's points where the maneuver begins and ends */
  beginIndex: number;
  endIndex: number;
  /** In kilometers */
  length: number;
  /** In seconds */
  time: number;
};

export type Route = {
  /** The waypoints the route was requested through */
  waypoints: Coords[];
  coordinates: Coords[];
  maneuvers: RouteManeuver[];
  /** In kilometers */
  totalDistance: number;
  /** In seconds */
  totalTime: number;
};

/** An error response of the routing server, e.g. when there's no road near a waypoint */
export class RoutingServerError extends Error {
  constructor(
    message: string,
    /** The server's response */
    readonly response: unknown
  ) {
    super(message);
    this.name = "RoutingServerError";
  }
}

const isServerErrorResponse = (data: unknown): data is { error: string } =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as { error?: unknown }).error === "string";

/** Joins the legs of the trip (one between each two waypoints) into one route */
const parseRoute = (waypoints: Coords[], data: unknown): Route => {
  const trip = (data as Partial<RoutingAPI> | null)?.trip;
  if (!trip || !Array.isArray(trip.legs) || trip.legs.length === 0) {
    throw new Error("The routing server's response has no route");
  }
  if (trip.status !== undefined && trip.status !== 0) {
    throw new Error(trip.status_message ?? `Routing status ${trip.status}`);
  }

  const coordinates: Coords[] = [];
  const maneuvers: RouteManeuver[] = [];
  for (const leg of trip.legs) {
    const shape = decodePolyline(leg.shape, 6);
    const last = coordinates.at(-1);
    // A leg starts at the waypoint where the previous one ends, the point is kept once
    const isJoined =
      last !== undefined &&
      shape.length > 0 &&
      last[0] === shape[0][0] &&
      last[1] === shape[0][1];
    const offset = isJoined ? coordinates.length - 1 : coordinates.length;
    coordinates.push(...(isJoined ? shape.slice(1) : shape));
    for (const maneuver of leg.maneuvers) {
      maneuvers.push({
        beginIndex: offset + maneuver.begin_shape_index,
        endIndex: offset + maneuver.end_shape_index,
        length: maneuver.length,
        time: maneuver.time,
      });
    }
  }

  return {
    waypoints,
    coordinates,
    maneuvers,
    totalDistance: trip.summary?.length ?? 0,
    totalTime: trip.summary?.time ?? 0,
  };
};

/**
 * Requests a route through the waypoints from the Valhalla routing server (`<VITE_NAV_API_BASE>/route`).
 * Rejects with a `RoutingServerError` when the server answers with an error, with axios' error when it can't be
 * reached, times out or the request is aborted through the signal.
 */
export const fetchRoute = async (
  { waypoints, costing, costingOptions }: RouteRequest,
  signal?: AbortSignal
): Promise<Route> => {
  const params = {
    costing,
    costing_options: costingOptions,
    locations: waypoints.map(([lat, lon]) => ({ lat, lon })),
  };

  try {
    const { data } = await uninterceptedAxiosInstance.get<unknown>(
      `${NAV_BASE_API_URL}/route`,
      { params: { json: JSON.stringify(params) }, signal }
    );
    return parseRoute(waypoints, data);
  } catch (error) {
    const data: unknown = axios.isAxiosError(error)
      ? error.response?.data
      : undefined;
    if (isServerErrorResponse(data)) {
      throw new RoutingServerError(data.error, data);
    }
    throw error;
  }
};
